import { useMemo } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';
import { useTracking } from '@change-corgi/core-react/tracking';

import { useHomepagePageData } from '#src/app/pages/homepage/pageContext';

export type PetitionMarqueeCardData = {
	key: string;
	title: string;
	imageUrl: string;
	signaturesLabel: string;
	href: string;
	isVictory: boolean;
};

type Result = ModelHookResult<
	{
		petitions: PetitionMarqueeCardData[];
	},
	{
		trackPetitionClick: (petitionId: string) => void;
	}
>;

export function usePetitionMarquee(): Result {
	const {
		data: { petitionMarqueeItems },
	} = useHomepagePageData();
	const { translatePlural } = useI18n();
	const track = useTracking();

	// memoized so useMarqueeRows only re-samples when the page data actually changes
	const petitions = useMemo(
		() =>
			petitionMarqueeItems.flatMap(({ summarizedTitle, petition }) => {
				const { displayed, displayedLocalized } = petition.signatureState.signatureCount;

				// the starter's avatar, else the petition photo; a petition with neither is dropped
				// rather than shown with a placeholder image
				const imageUrl = petition.user.photo?.userMedium.url || petition.photo?.petitionSquare.url;
				if (!imageUrl) return [];

				return [
					{
						key: petition.id,
						title: summarizedTitle,
						imageUrl,
						signaturesLabel: translatePlural('corgi.components.petition_card.signatures', displayed, {
							countLocalized: displayedLocalized,
						}),
						href: `/p/${encodeURIComponent(petition.slug)}?source_location=homepage_petition_marquee`,
						isVictory: petition.status === 'VICTORY',
					},
				];
			}),
		[petitionMarqueeItems, translatePlural],
	);

	return {
		data: {
			petitions,
		},
		actions: {
			trackPetitionClick: (petitionId: string) => {
				void track('homepage_petition_marquee_click', { petition_id: petitionId });
			},
		},
	};
}
